/**
 * Parts Counterperson Performance Report Renderer
 *
 * Consolidated-only, month-range view of per-person parts counter metrics —
 * the same monthly data behind the Forecast → Parts → Parts Counter
 * Performance card, summed over the selected months. Company-wide summary on
 * top, then one section per location with persons ranked by gross. Clicking a
 * row expands the 6-pay-type breakdown (mirrors the Forecast card's
 * PartsCounterRow); rows print collapsed (totals only).
 */

import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { LOCATIONS } from '../../LocationDropdown';

export interface ReportPayTypeMetrics {
  sales: number;
  cost: number;
  gross: number;
  gpPct: number | null;
}

/** One person's totals over the month range — mirrors the backend PartsCounterReportPerson. */
export interface PartsCounterReportPerson {
  employeeId: string;
  employeeName: string;
  locationId: number;
  salesTotal: number;
  costTotal: number;
  grossTotal: number;
  gpPctTotal: number | null;
  transactionCount: number;
  lostSalesCount: number;
  counterRetail: ReportPayTypeMetrics;
  counterWholesale: ReportPayTypeMetrics;
  customerParts: ReportPayTypeMetrics;
  internalParts: ReportPayTypeMetrics;
  warrantyParts: ReportPayTypeMetrics;
  other: ReportPayTypeMetrics;
}

export interface PartsCounterReportLocation {
  locationId: number;
  personCount: number;
  totalSales: number;
  totalCost: number;
  totalGross: number;
  totalTransactions: number;
  totalLostSales: number;
  persons: PartsCounterReportPerson[];
}

export interface PartsCounterReportData {
  startMonth: string; // YYYY-MM
  endMonth: string;   // YYYY-MM
  companySummary: {
    personCount: number;
    totalSales: number;
    totalCost: number;
    totalGross: number;
    totalTransactions: number;
    totalLostSales: number;
  };
  locations: PartsCounterReportLocation[];
}

export const PAY_TYPE_LABELS: Array<{
  key: keyof Pick<PartsCounterReportPerson, 'counterRetail' | 'counterWholesale' | 'customerParts' | 'internalParts' | 'warrantyParts' | 'other'>;
  label: string;
}> = [
  { key: 'counterRetail', label: 'Counter Retail' },
  { key: 'counterWholesale', label: 'Counter Wholesale' },
  { key: 'customerParts', label: 'Customer Parts' },
  { key: 'warrantyParts', label: 'Warranty Parts' },
  { key: 'internalParts', label: 'Internal Parts' },
  { key: 'other', label: 'Other' },
];

export function getReportLocationName(locationId: number): string {
  return LOCATIONS.find((loc) => loc.id === locationId)?.name || `Location ${locationId}`;
}

/** Format a YYYY-MM month as e.g. "Jan 2026". */
export function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
}

const SummaryTile: React.FC<{ label: string; value: string; accent?: boolean }> = ({
  label,
  value,
  accent = false,
}) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3">
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className={`text-lg font-bold ${accent ? 'text-green-600' : 'text-gray-900'}`}>{value}</p>
  </div>
);

const PersonRow: React.FC<{
  person: PartsCounterReportPerson;
  rank: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ person, rank, isExpanded, onToggle }) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <div
      className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-gray-400 print:hidden">
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
        </span>
        <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
        <span className="font-medium text-gray-900 truncate">{person.employeeName}</span>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="text-right text-sm w-20 hidden sm:block">
          <span className="text-gray-500">{formatPercent(person.gpPctTotal)}</span>
          <span className="text-gray-400 text-xs ml-1">GP</span>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-green-600">
            {formatCurrency(person.grossTotal)}
          </span>
        </div>
        <div className="text-right text-sm w-28">
          <span className="text-gray-900">{person.transactionCount}</span>
          <span className="text-gray-400 text-xs ml-1">tickets</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className={person.lostSalesCount > 0 ? 'text-red-600' : 'text-gray-400'}>
            {person.lostSalesCount}
          </span>
          <span className="text-gray-400 text-xs ml-1">lost</span>
        </div>
      </div>
    </div>

    {isExpanded && (
      <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
              <th className="text-left py-2 px-3 font-medium">Pay Type</th>
              <th className="text-right py-2 px-3 font-medium">Sales</th>
              <th className="text-right py-2 px-3 font-medium">Cost</th>
              <th className="text-right py-2 px-3 font-medium">Gross</th>
              <th className="text-right py-2 px-3 font-medium">GP%</th>
            </tr>
          </thead>
          <tbody>
            {PAY_TYPE_LABELS.map(({ key, label }) => {
              const metrics = person[key];
              if (metrics.sales === 0 && metrics.cost === 0 && metrics.gross === 0) {
                return null;
              }
              return (
                <tr key={key} className="border-t border-gray-200 hover:bg-white">
                  <td className="py-2 px-3 text-gray-700">{label}</td>
                  <td className="py-2 px-3 text-right text-gray-900">{formatCurrency(metrics.sales)}</td>
                  <td className="py-2 px-3 text-right text-gray-900">{formatCurrency(metrics.cost)}</td>
                  <td className="py-2 px-3 text-right text-gray-900 font-medium">{formatCurrency(metrics.gross)}</td>
                  <td className="py-2 px-3 text-right text-gray-900">{formatPercent(metrics.gpPct)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
              <td className="py-2 px-3 text-gray-700">Total</td>
              <td className="py-2 px-3 text-right text-gray-900">{formatCurrency(person.salesTotal)}</td>
              <td className="py-2 px-3 text-right text-gray-900">{formatCurrency(person.costTotal)}</td>
              <td className="py-2 px-3 text-right text-green-600 font-bold">{formatCurrency(person.grossTotal)}</td>
              <td className="py-2 px-3 text-right text-gray-900">{formatPercent(person.gpPctTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const PartsCounterReport: React.FC<{ data: PartsCounterReportData }> = ({ data }) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const locations = data.locations || [];

  if (locations.length === 0) {
    return (
      <div className="text-center py-12">
        <CircleStackIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">No parts counter data found for the selected month range.</p>
      </div>
    );
  }

  const summary = data.companySummary;

  return (
    <div className="space-y-6">
      {/* Company-wide summary */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          All Locations — {formatMonthLabel(data.startMonth)}
          {data.endMonth !== data.startMonth ? ` to ${formatMonthLabel(data.endMonth)}` : ''}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryTile label="Counterpeople" value={String(summary.personCount)} />
          <SummaryTile label="Total Gross" value={formatCurrency(summary.totalGross)} accent />
          <SummaryTile label="Total Sales" value={formatCurrency(summary.totalSales)} />
          <SummaryTile label="Total Cost" value={formatCurrency(summary.totalCost)} />
          <SummaryTile label="Transactions" value={summary.totalTransactions.toLocaleString('en-US')} />
          <SummaryTile label="Lost Sales" value={summary.totalLostSales.toLocaleString('en-US')} />
        </div>
      </div>

      {/* Per-location sections */}
      {locations.map((location) => (
        <div
          key={location.locationId}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden print:break-inside-avoid"
        >
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-semibold text-gray-900">
              {getReportLocationName(location.locationId)}
            </h4>
            <div className="text-sm text-gray-600 flex items-center gap-4">
              <span>
                <span className="font-semibold text-green-600">{formatCurrency(location.totalGross)}</span>
                <span className="text-gray-400 text-xs ml-1">gross</span>
              </span>
              <span>
                {location.totalTransactions.toLocaleString('en-US')}
                <span className="text-gray-400 text-xs ml-1">tickets</span>
              </span>
              <span>
                <span className={location.totalLostSales > 0 ? 'text-red-600' : ''}>
                  {location.totalLostSales.toLocaleString('en-US')}
                </span>
                <span className="text-gray-400 text-xs ml-1">lost</span>
              </span>
            </div>
          </div>
          <div>
            {location.persons.map((person, i) => {
              const key = `${location.locationId}#${person.employeeId}`;
              return (
                <PersonRow
                  key={key}
                  person={person}
                  rank={i + 1}
                  isExpanded={expandedKeys.has(key)}
                  onToggle={() => toggle(key)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
